import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import { useCommandPalette } from '../context/CommandPaletteContext';
import { usePinnedResources } from '../hooks/usePinnedResources';
import { useResourcePageTitle } from '../hooks/useResourcePageTitle';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import HubCard, {
    HUB_LINK_COUNT,
    HubCardFrame,
    HubIconSquare,
    type Hub
} from '../components/HubCard';
import PinnedCard from '../components/PinnedCard';
import ResourcesPageHeader from '../components/ResourcesPageHeader';
import SearchTrigger from '../components/SearchTrigger';
import { ResourceError, ResourceLoading } from '../components/ResourceStates';

/** Quick-ask chips are drawn from the shortest real questions in the FAQ. */
const QUICK_ASK_COUNT = 4;
const QUICK_ASK_MAX_LENGTH = 48;

const HUB_TITLES: Record<Hub['kind'], string> = {
    document: 'Help Center',
    video: 'Videos',
    faq: 'FAQ'
};

const HUB_ORDER: Array<Hub['kind']> = ['document', 'video', 'faq'];

const HUB_SEGMENTS: Record<Hub['kind'], string> = {
    document: 'help-center',
    video: 'videos',
    faq: 'faq'
};

/** Landing hub: stats, search, and the 2×2 "Start here" grid (three sections + pins). */
export const ResourcesOverviewPage: React.FC = () => {
    const { basePath } = useResourcesConfig();
    const { open } = useCommandPalette();
    const { entries, videos, faqItems, isLoading, error, refresh } = useResourcesLibrary();
    const { filterPinned } = usePinnedResources();

    useResourcePageTitle('Resources');

    const hubs = useMemo<Hub[]>(
        () =>
            HUB_ORDER.map((kind) => {
                const ofKind = entries
                    .filter((entry) => entry.kind === kind)
                    .sort((a, b) => b.timestamp - a.timestamp);
                return {
                    kind,
                    title: HUB_TITLES[kind],
                    to: `${basePath}/${HUB_SEGMENTS[kind]}`,
                    count: ofKind.length,
                    links: ofKind.slice(0, HUB_LINK_COUNT)
                };
            }),
        [entries, basePath]
    );

    const pinned = useMemo(() => filterPinned(entries), [filterPinned, entries]);

    const quickAsks = useMemo(
        () =>
            faqItems
                .filter((item) => item.question.length <= QUICK_ASK_MAX_LENGTH)
                .slice(0, QUICK_ASK_COUNT),
        [faqItems]
    );

    const stats = useMemo(
        () => [
            { value: entries.length, label: 'resources' },
            { value: videos.length, label: 'training videos' },
            { value: pinned.length, label: 'pinned by you' }
        ],
        [entries.length, videos.length, pinned.length]
    );

    if (isLoading && entries.length === 0) {
        return (
            <div className="flex-1 min-h-0">
                <ResourceLoading label="Loading resources…" />
            </div>
        );
    }

    if (error && entries.length === 0) {
        return (
            <div className="flex-1 min-h-0">
                <ResourceError message={error} onRetry={() => refresh(true)} />
            </div>
        );
    }

    return (
        // The shell hands scrolling to the page so the layout stays viewport-locked.
        <div className="flex flex-col min-h-0 flex-1">
            <ResourcesPageHeader title="Resources" showSearch={false} />

            <div className="flex-1 overflow-y-auto scrollbar-hide flex flex-col gap-6 px-8 pt-6 pb-16">
                {/* Stat cards, in the same shape as the patients overview */}
                <div className="flex gap-4 flex-wrap">
                    {stats.map((stat) => (
                        <div
                            key={stat.label}
                            className="flex-1 min-w-[160px] flex flex-col justify-center px-5 py-3.5 rounded-xl bg-white border-[1.5px] border-gray-200 shadow-sm"
                        >
                            <span className="text-[11px] font-semibold tracking-[0.04em] uppercase text-gray-400">
                                {stat.label}
                            </span>
                            <span className="mt-1 text-[26px] font-bold leading-none text-gray-800 tabular-nums">
                                {stat.value}
                            </span>
                        </div>
                    ))}
                </div>

                <HubCardFrame
                    icon={
                        <HubIconSquare>
                            <MagnifyingGlassIcon className="w-4 h-4" />
                        </HubIconSquare>
                    }
                    title={
                        <span className="text-base font-bold text-gray-800">Search everything</span>
                    }
                >
                    <p className="text-sm leading-relaxed text-gray-500">
                        One search across the Help Center, videos and the FAQ.
                    </p>

                    <SearchTrigger
                        variant="full"
                        className="flex-shrink-0 border-gray-300"
                        placeholder="Search resources…"
                    />

                    {quickAsks.length > 0 && (
                        <div className="flex gap-2 flex-wrap items-center border-t border-gray-200 pt-4">
                            <span className="text-xs text-gray-400">Popular questions</span>
                            {quickAsks.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => open(item.question)}
                                    className="px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    {item.question}
                                </button>
                            ))}
                        </div>
                    )}
                </HubCardFrame>

                {/* Start here */}
                <section className="flex flex-col gap-4">
                    <h2 className="text-lg font-bold text-gray-800">Start here</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {hubs.map((hub) => (
                            <HubCard key={hub.kind} hub={hub} />
                        ))}
                        <PinnedCard entries={pinned} />
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ResourcesOverviewPage;
